import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePublicSettings } from "../hooks/usePublicSettings";
import { usePublicLocations } from "../hooks/usePublicLocations";
import VoucherInput from "../components/VoucherInput";
import DateInput from "../components/DateInput";
import VehicleGallery from "../components/vehicle-detail/VehicleGallery";
import AddonPicker from "../components/vehicle-detail/AddonPicker";
import CheckoutFields from "../components/vehicle-detail/CheckoutFields";
import PriceSummary from "../components/vehicle-detail/PriceSummary";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const publicSettings = usePublicSettings();
  const locations = usePublicLocations();
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState({ available: true });
  const [extras, setExtras] = useState([]); // dynamic — filtered to this vehicle's Car Group

  const today = new Date().toISOString().slice(0, 10);
  const leadDays = Number(publicSettings?.release_date_days ?? 0);
  const minPickup = leadDays > 0
    ? new Date(Date.now() + 86400000 * leadDays).toISOString().slice(0, 10)
    : today;
  const tomorrow = new Date(Date.now() + 86400000 * Math.max(leadDays + 1, 3)).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    pickup_date: minPickup,
    return_date: tomorrow,
    pickup_time: "10:00",
    return_time: "10:00",
    pickup_location: "Main Office",
    return_location: "Main Office",
    full_name: "",
    phone: "",
    license_number: "",
    addons: [],
    voucher_code: "",
  });
  const [voucherPreview, setVoucherPreview] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => setError("Not found"));
    // Fetch extras scoped to this vehicle's Car Group (falls back to all
    // active extras when the group has no allowed_extra_ids).
    api.get(`/extras?status=active&vehicle_id=${id}`)
      .then((r) => setExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExtras([]));
  }, [id]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.name || "" }));
  }, [user]);

  // Once locations load, pick a sensible default: Main Office → any
  // downtown-typed location → first active. This keeps the customer flow
  // from defaulting to Airport (which triggers flight-detail requirements).
  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) return;
    const fallback =
      locations.find((l) => (l.name || "").toLowerCase() === "main office")?.name
      || locations.find((l) => (l.location_type || "downtown") === "downtown")?.name
      || locations[0]?.name;
    if (!fallback) return;
    setForm((f) => {
      const hasPickup = locations.some((l) => l.name === f.pickup_location);
      const hasReturn = locations.some((l) => l.name === f.return_location);
      if (hasPickup && hasReturn) return f;
      return {
        ...f,
        pickup_location: hasPickup ? f.pickup_location : fallback,
        return_location: hasReturn ? f.return_location : fallback,
      };
    });
  }, [locations]);

  // Server-side authoritative price quote — single source of truth.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    if (!id || !form.pickup_date || !form.return_date) return;
    if (form.return_date <= form.pickup_date) {
      setAvailability({ available: false, reason: "Return date must be after pickup date" });
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const timer = setTimeout(() => {
      api.post("/bookings/quote", {
        vehicle_id: id,
        pickup_date: form.pickup_date,
        return_date: form.return_date,
        pickup_time: form.pickup_time,
        return_time: form.return_time,
        pickup_location: form.pickup_location,
        return_location: form.return_location,
        addons: form.addons,
        voucher_code: form.voucher_code || null,
      })
        .then((r) => {
          if (cancelled) return;
          setQuote(r.data);
          const warn = (r.data?.warnings || []);
          const conflict = warn.find((w) => w.code === "conflict");
          if (conflict) {
            setAvailability({ available: false, reason: conflict.message });
          } else {
            setAvailability({ available: true });
          }
        })
        .catch(() => { if (!cancelled) setQuote(null); })
        .finally(() => { if (!cancelled) setQuoteLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id, form.pickup_date, form.return_date, form.pickup_time, form.return_time,
      form.pickup_location, form.return_location, form.addons, form.voucher_code]);

  if (error) return <div className="p-12 text-center text-neutral-500">Vehicle not found.</div>;
  if (!v) return <div className="p-12 text-center text-neutral-500">Loading…</div>;

  // Deposit derivation for the summary component. Every other price
  // number is inside `quote.*` and read directly by PriceSummary.
  const securityDeposit = quote?.security_deposit ?? 0;
  const depositPct = quote?.security_deposit_pct ?? Number(v.security_deposit_pct || 0);

  const toggleAddon = (id) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(id) ? f.addons.filter((x) => x !== id) : [...f.addons, id],
    }));

  const handleBook = async (e) => {
    e.preventDefault();
    setError("");
    if (!user) {
      navigate(`/login?redirect=/vehicles/${id}`);
      return;
    }
    try {
      const { data } = await api.post("/bookings", { vehicle_id: id, ...form });
      navigate(`/booking/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="vehicle-detail-page">
      <Link to="/vehicles" className="text-xs uppercase tracking-wider text-neutral-500 link-underline">
        ← Back to fleet
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mt-8">
        {/* LEFT — Visuals & info */}
        <VehicleGallery v={v} />

        {/* RIGHT — Booking form */}
        <div className="lg:col-span-5">
          <form
            onSubmit={handleBook}
            className="bg-white border border-black/10 p-6 md:p-8 sticky top-28"
            data-testid="booking-form"
          >
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="overline" data-testid="vd-rate-label">
                  {v.has_duration_tiers ? "Tier 1 rate" : "Daily rate (default)"}
                </p>
                <p className="font-display text-4xl" data-testid="vd-rate">
                  €{Number((v.has_duration_tiers ? v.display_rate : v.daily_rate) || 0).toFixed(0)}
                  <span className="text-lg text-neutral-500">/day</span>
                </p>
                {v.has_duration_tiers && Number(v.daily_rate) > 0 && Number(v.daily_rate) !== Number(v.display_rate) && (
                  <p
                    className="text-[11px] text-neutral-500 mt-1"
                    data-testid="vd-rate-base"
                    title="Base / default daily rate — used when no tier covers the rental length"
                  >
                    Base rate: €{Number(v.daily_rate).toFixed(0)}/day
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <Field label="Pickup date">
                <DateInput required min={minPickup} value={form.pickup_date} onChange={(v) => setForm({ ...form, pickup_date: v })} testId="booking-pickup-date" />
              </Field>
              <Field label="Return date">
                <DateInput required min={minPickup} value={form.return_date} onChange={(v) => setForm({ ...form, return_date: v })} testId="booking-return-date" />
              </Field>
              <Field label="Pickup time">
                <input type="time" required value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} className="editorial-input w-full" data-testid="booking-pickup-time" />
              </Field>
              <Field label="Return time">
                <input type="time" required value={form.return_time} onChange={(e) => setForm({ ...form, return_time: e.target.value })} className="editorial-input w-full" data-testid="booking-return-time" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <Field label="From">
                <select value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} className="editorial-input w-full" data-testid="booking-pickup-location" required>
                  {locations.map((l) => (
                    <option key={l.id} value={l.name}>{l.name}{l.pickup_fee > 0 ? ` (+${l.pickup_fee})` : ""}</option>
                  ))}
                </select>
              </Field>
              <Field label="To">
                <select value={form.return_location} onChange={(e) => setForm({ ...form, return_location: e.target.value })} className="editorial-input w-full" data-testid="booking-return-location" required>
                  {locations.map((l) => (
                    <option key={l.id} value={l.name}>{l.name}{l.return_fee > 0 ? ` (+${l.return_fee})` : ""}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Full Name">
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="editorial-input w-full" required data-testid="booking-full-name" />
            </Field>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="editorial-input w-full" required data-testid="booking-phone" />
              </Field>
              <Field label="License #">
                <input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="editorial-input w-full" required data-testid="booking-license" />
              </Field>
            </div>

            <div className="mt-6">
              <CheckoutFields form={form} setForm={setForm} locations={locations} />
            </div>

            <AddonPicker
              extras={extras}
              selected={form.addons}
              onToggle={toggleAddon}
            />

            <VoucherInput
              code={form.voucher_code}
              onCodeChange={(c) => setForm((f) => ({ ...f, voucher_code: c }))}
              onPreviewChange={setVoucherPreview}
              disabled={!user || !availability.available}
              context={{
                vehicle_id: id,
                pickup_date: form.pickup_date,
                return_date: form.return_date,
                pickup_time: form.pickup_time,
                return_time: form.return_time,
                pickup_location: form.pickup_location,
                return_location: form.return_location,
                addons: form.addons,
              }}
            />

            <div className="divider-line my-6"></div>
            <PriceSummary
              quote={quote}
              quoteLoading={quoteLoading}
              form={form}
              voucherPreview={voucherPreview}
              dailyRate={v.daily_rate}
              taxRatePct={publicSettings?.tax_rate_pct ?? 10}
              securityDeposit={securityDeposit}
              depositPct={depositPct}
              availability={availability}
              leadDays={leadDays}
              minPickup={minPickup}
            />

            {error && <p className="text-red-600 text-sm mt-4" data-testid="booking-error">{error}</p>}

            <button
              type="submit"
              disabled={!availability.available}
              className="mt-6 w-full bg-black text-white py-4 hover:bg-neutral-800 transition-colors text-sm tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="proceed-to-checkout"
            >
              {user ? "Continue to Agreement" : "Sign in to Book"}
            </button>
            <p className="text-xs text-neutral-500 mt-3 text-center">Free cancellation up to 24 hours before pickup.</p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">{label}</span>
      {children}
    </label>
  );
}
